// Branded PDF download of a live/hybrid event agenda, shown on the public course
// page. Same pattern as roster-pdf.js: jsPDF + autotable load lazily so they stay
// out of the main bundle.

const BRAND = {
  orange: [240, 137, 35],
  darkOrange: [214, 116, 16],
  cream: [252, 230, 204],
  gray: [111, 111, 111],
  ink: [40, 46, 56],
  border: [228, 230, 235],
  white: [255, 255, 255],
};

const TYPE_COLORS = {
  keynote:  { fill: [254, 243, 199], text: [180, 83, 9] },
  breakout: { fill: [237, 233, 254], text: [109, 40, 217] },
  break:    { fill: [209, 250, 229], text: [4, 120, 87] },
  session:  { fill: [241, 245, 249], text: [71, 85, 105] },
};

// Continuing-education credit per agenda item, shared by the course page, the
// live player and the PDF. Outlined so it never reads as a session-type pill;
// 'none' (lunch, breaks) has no entry and gets no pill.
export const CE_CREDIT_META = {
  ceu:     { label: 'CEU',     pill: 'bg-white border-emerald-300 text-emerald-700', border: [110, 231, 183], text: [4, 120, 87] },
  non_ceu: { label: 'Non CEU', pill: 'bg-white border-slate-300 text-slate-500',     border: [203, 213, 225], text: [100, 116, 139] },
};

// Tag for sessions an admin marked "live only".
export const LIVE_ONLY_META = { label: 'Live only', pill: 'bg-white border-red-300 text-red-600' };

const LOGO_SRC = '/LearnableLogo_noTag.png';

async function loadImage(src) {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
    return dims ? { dataUrl, ...dims } : null;
  } catch {
    return null;
  }
}

// Times are stored as 24-hour "HH:MM" (what <input type="time"> produces, and
// what keeps the agenda sorting correctly) but shown in 12-hour form. Anything
// that isn't HH:MM is shown as entered.
export function formatClockTime(value) {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(value || '').trim());
  if (!match) return value || '';
  const hours = Number(match[1]);
  if (hours > 23) return value;
  return `${hours % 12 || 12}:${match[2]} ${hours < 12 ? 'AM' : 'PM'}`;
}

export function formatAgendaTime(item) {
  if (!item.start_time) return '';
  const start = formatClockTime(item.start_time);
  return item.end_time ? `${start} – ${formatClockTime(item.end_time)}` : start;
}

export function groupAgendaByDay(agenda) {
  const days = [...new Set(agenda.map((i) => i.day_number || 1))].sort((a, b) => a - b);
  return days.map((day) => {
    const items = agenda.filter((i) => (i.day_number || 1) === day);
    return { day, label: items.find((i) => i.day_label)?.day_label || `Day ${day}`, items };
  });
}

function eventDateLine(course) {
  if (!course.live_date_utc) return '';
  const d = new Date(course.live_date_utc);
  const date = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
  return `${date} at ${time} ET`;
}

export function renderAgendaPdf({ doc, autoTable, logo, course, generatedOn }) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  const agenda = course.agenda || [];

  // ── Header: wordmark (left) + document label (right) ──
  if (logo && logo.dataUrl) {
    const logoH = 30;
    doc.addImage(logo.dataUrl, 'PNG', margin, margin, logo.w * (logoH / logo.h), logoH);
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(22);
    doc.setTextColor(...BRAND.gray); doc.text('learn', margin, margin + 22);
    const w = doc.getTextWidth('learn');
    doc.setTextColor(...BRAND.orange); doc.text('able', margin + w, margin + 22);
  }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(...BRAND.ink);
  doc.text('Event Agenda', pageW - margin, margin + 13, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...BRAND.gray);
  doc.text('learnableskill.com', pageW - margin, margin + 27, { align: 'right' });

  const ruleY = margin + 44;
  doc.setDrawColor(...BRAND.orange); doc.setLineWidth(2);
  doc.line(margin, ruleY, pageW - margin, ruleY);

  // ── Event title, date, venue ──
  let y = ruleY + 24;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...BRAND.ink);
  const titleLines = doc.splitTextToSize(course.title || 'Event', pageW - margin * 2);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 17;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...BRAND.gray);
  const details = [
    eventDateLine(course),
    [course.venue_name, course.venue_address].filter(Boolean).join(', '),
    course.hosted_by ? `Hosted by ${course.hosted_by}${course.hosted_by_title ? `, ${course.hosted_by_title}` : ''}` : '',
  ].filter(Boolean);
  for (const line of details) {
    const wrapped = doc.splitTextToSize(line, pageW - margin * 2);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 14;
  }
  y += 8;

  // ── Agenda table ──
  const speakerNames = (item) => (item.speakers || []).map((s) => s.name).join('\n');
  const showSpeaker = agenda.some((i) => speakerNames(i));
  const showLocation = agenda.some((i) => i.location);
  const columns = ['Time', 'Type', 'Session', ...(showSpeaker ? ['Speaker'] : []), ...(showLocation ? ['Location'] : [])];
  const span = columns.length;
  const days = groupAgendaByDay(agenda);
  const multiDay = days.length > 1;

  const body = [];
  for (const { label, items } of days) {
    if (multiDay) {
      body.push([{ content: label, colSpan: span, styles: { fillColor: BRAND.cream, textColor: BRAND.darkOrange, fontStyle: 'bold', fontSize: 9.5 } }]);
    }
    for (const item of items) {
      const type = TYPE_COLORS[item.session_type] ? item.session_type : 'session';
      const hasCredit = !!CE_CREDIT_META[item.ce_credit];
      body.push([
        formatAgendaTime(item) || '-',
        // A blank second line makes room for the credit pill drawn in didDrawCell.
        { content: hasCredit ? `${type.toUpperCase()}\n ` : type.toUpperCase(), ceCredit: item.ce_credit, styles: { fillColor: TYPE_COLORS[type].fill, textColor: TYPE_COLORS[type].text, fontStyle: 'bold', fontSize: 7, halign: 'center' } },
        { content: item.live_only ? `${item.title}  (Live only)` : item.title, styles: { fontStyle: 'bold' } },
        ...(showSpeaker ? [speakerNames(item)] : []),
        ...(showLocation ? [item.location || ''] : []),
      ]);
      if (item.description) {
        body.push([
          '',
          '',
          { content: item.description, colSpan: span - 2, styles: { textColor: BRAND.gray, fontSize: 8.5, cellPadding: { top: 0, right: 6, bottom: 7, left: 6 } } },
        ]);
      }
    }
  }

  autoTable(doc, {
    head: [columns],
    body,
    startY: y,
    margin: { left: margin, right: margin, bottom: 40 },
    theme: 'plain',
    styles: { font: 'helvetica', fontSize: 9.5, cellPadding: 6, textColor: BRAND.ink, overflow: 'linebreak', valign: 'top' },
    headStyles: { fillColor: BRAND.orange, textColor: BRAND.white, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 112, textColor: BRAND.gray },
      1: { cellWidth: 60 },
      ...(showSpeaker ? { 3: { cellWidth: showLocation ? 100 : 130 } } : {}),
      ...(showLocation ? { [showSpeaker ? 4 : 3]: { cellWidth: 90 } } : {}),
    },
    // Hairline under each agenda item (skipping the row that carries its description)
    didDrawCell: (data) => {
      if (data.section !== 'body') return;
      const credit = data.column.index === 1 && CE_CREDIT_META[data.cell.raw?.ceCredit];
      if (credit) {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5);
        const w = doc.getTextWidth(credit.label) + 8;
        const h = 10;
        const x = data.cell.x + (data.cell.width - w) / 2;
        const y = data.cell.y + 17;
        doc.setFillColor(...BRAND.white); doc.setDrawColor(...credit.border); doc.setLineWidth(0.6);
        doc.roundedRect(x, y, w, h, 2, 2, 'FD');
        doc.setTextColor(...credit.text);
        doc.text(credit.label, x + w / 2, y + 7, { align: 'center' });
      }
      const next = body[data.row.index + 1];
      const nextIsDescription = next && next[0] === '' && next[1] === '';
      if (nextIsDescription) return;
      doc.setDrawColor(...BRAND.border); doc.setLineWidth(0.5);
      doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
    },
    didDrawPage: () => {
      const page = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...BRAND.gray);
      doc.text(`Generated ${generatedOn}`, margin, pageH - 18);
      doc.text('Schedule subject to change', pageW / 2, pageH - 18, { align: 'center' });
      doc.text(`Page ${page} of {tp}`, pageW - margin, pageH - 18, { align: 'right' });
    },
  });
  if (typeof doc.putTotalPages === 'function') doc.putTotalPages('{tp}');
}

export async function downloadAgendaPdf(course) {
  const { jsPDF } = await import('jspdf');
  const { autoTable } = await import('jspdf-autotable');
  const logo = await loadImage(LOGO_SRC);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const generatedOn = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  renderAgendaPdf({ doc, autoTable, logo, course, generatedOn });
  doc.save(`${course.slug || 'event'}-agenda.pdf`);
}
